import { apiClient, normalizeAuthToken, pathSegment } from "@/lib/api/client";
import { setStoredAuthToken } from "@/lib/auth-storage";
import { isAdminSubdomain } from "@/lib/subdomain-routing";
import {
  User,
  AuthResponse,
  FrejaAuthStatus,
  LoginRequest,
  RegisterRequest,
  RegisterResponse,
  GoogleAuthRequest,
  FrejaRegisterResponse,
  ChangePasswordRequest,
  UpdateUserRequest,
  StartPasswordResetRequest,
  PasswordResetFinalRequest,
  VerifyEmailRequest,
  GoogleRegisterResponse,
  QuickRegisterRequest,
  QuickRegisterResponse,
  FrejaAuthRef,
  UserDeleteFailureDTO,
  BankIdStartResponse,
  BankIdPollResponse,
  IdentityProviderAvailability,
} from "@/types";

type AuthResponseLike = Partial<AuthResponse> & Record<string, unknown>;
type JwtPayload = {
  sub?: string;
  uid?: number | string;
  accountType?: string;
};

const TOKEN_KEYS = [
  "accessToken",
  "token",
  "access_token",
  "jwt",
  "bearerToken",
] as const;

const SWEDISH_CITY_ENUMS: Record<string, string> = {
  GÖTEBORG: "GÖTEBORG",
  GOTEBORG: "GÖTEBORG",
  STOCKHOLM: "STOCKHOLM",
  LUND: "LUND",
  UPPSALA: "UPPSALA",
  MALMÖ: "MALMÖ",
  MALMO: "MALMÖ",
  LINKÖPING: "LINKÖPING",
  LINKOPING: "LINKÖPING",
  ÖREBRO: "ÖREBRO",
  OREBRO: "ÖREBRO",
  VÄSTERÅS: "VÄSTERÅS",
  VASTERAS: "VÄSTERÅS",
  HELSINGBORG: "HELSINGBORG",
  JÖNKÖPING: "JÖNKÖPING",
  JONKOPING: "JÖNKÖPING",
  NORRKÖPING: "NORRKÖPING",
  NORRKOPING: "NORRKÖPING",
  HALMSTAD: "HALMSTAD",
  LULEÅ: "LULEÅ",
  LULEA: "LULEÅ",
  SUNDSVALL: "SUNDSVALL",
  VÄXJÖ: "VÄXJÖ",
  VAXJO: "VÄXJÖ",
  BORÅS: "BORÅS",
  BORAS: "BORÅS",
  ESKILSTUNA: "ESKILSTUNA",
  GÄVLE: "GÄVLE",
  GAVLE: "GÄVLE",
  TROLLHÄTTAN: "TROLLHÄTTAN",
  TROLLHATTAN: "TROLLHÄTTAN",
  SKÖVDE: "SKÖVDE",
  SKOVDE: "SKÖVDE",
  KARLSTAD: "KARLSTAD",
  SKELLEFTEÅ: "SKELLEFTEÅ",
  SKELLEFTEA: "SKELLEFTEÅ",
  KALMAR: "KALMAR",
  KATRINEHOLM: "KATRINEHOLM",
  FALUN: "FALUN",
  KUNGSBACKA: "KUNGSBACKA",
  VARBERG: "VARBERG",
  LIDKÖPING: "LIDKÖPING",
  LIDKOPING: "LIDKÖPING",
  MOTALA: "MOTALA",
  UDDEVALLA: "UDDEVALLA",
  STRÖMSTAD: "STRÖMSTAD",
  STROMSTAD: "STRÖMSTAD",
  ESLÖV: "ESLÖV",
  ESLOV: "ESLÖV",
};

const compactObject = <T extends object>(value: T) =>
  Object.fromEntries(
    Object.entries(value).filter(([, entryValue]) => entryValue !== undefined)
  ) as Partial<T>;

const firstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return undefined;
};

const firstNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return undefined;
};

function decodeJwtPayload(token: string | null): JwtPayload {
  if (!token) return {};

  try {
    const payload = token.split(".")[1];
    if (!payload) return {};

    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );
    const decoded =
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(padded, "base64").toString("utf8");

    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return {};
  }
}

function normalizeAccountTypeValue(value: string | undefined): User["accountType"] | undefined {
  const accountType = value?.toLowerCase();
  if (!accountType) {
    return undefined;
  }

  if (
    accountType === "student" ||
    accountType === "company" ||
    accountType === "private_landlord" ||
    accountType === "landlord" ||
    accountType === "quick_register" ||
    accountType === "admin"
  ) {
    return accountType;
  }

  return undefined;
}

export function getAuthResponseToken(response: Partial<AuthResponse>): string {
  const responseLike = response as AuthResponseLike;
  const source = TOKEN_KEYS.map((key) => responseLike[key]).find(
    (value) => typeof value === "string" && value.trim().length > 0
  );

  const token = normalizeAuthToken(source);
  if (!token) {
    throw new Error("Inloggningen lyckades men backend skickade ingen JWT-token.");
  }

  return token;
}

export function getOptionalAuthResponseToken(
  response: Partial<AuthResponse>
): string | null {
  const responseLike = response as AuthResponseLike;
  const source = TOKEN_KEYS.map((key) => responseLike[key]).find(
    (value) => typeof value === "string" && value.trim().length > 0
  );

  return normalizeAuthToken(source);
}

function createUserFromFlatAuthResponse(
  response: Record<string, unknown>,
  token: string
): User {
  const jwtPayload = decodeJwtPayload(token);
  const firstName = firstString(response.firstName);
  const surname = firstString(response.surname);
  const email = firstString(response.email, jwtPayload.sub) ?? "";
  const accountType = firstString(response.accountType, jwtPayload.accountType);
  const id = firstNumber(response.id, response.userId, jwtPayload.uid) ?? 0;
  const displayName =
    firstString(response.displayName) ||
    [firstName, surname].filter(Boolean).join(" ") ||
    firstString(response.companyName, response.fullName, email) ||
    "Användare";

  return {
    ...(response as Partial<User>),
    id,
    email,
    accountType: normalizeAccountTypeValue(accountType) ?? "quick_register",
    displayName,
    createdAt: firstString(response.createdAt) ?? new Date(0).toISOString(),
    verified:
      typeof response.verified === "boolean"
        ? response.verified
        : Boolean(response.verifiedStudent ?? response.verifiedIdentity),
    firstName,
    surname,
  };
}

export function normalizeAuthResponse(
  response: Partial<AuthResponse> & Record<string, unknown>
): AuthResponse {
  const token = getAuthResponseToken(response);

  return {
    accessToken: token,
    user:
      response.user && typeof response.user === "object"
        ? (response.user as User)
        : createUserFromFlatAuthResponse(response, token),
  };
}

export function authResponseFromGoogleRegisterResponse(
  response: GoogleRegisterResponse
): AuthResponse {
  return normalizeAuthResponse(
    response as unknown as Partial<AuthResponse> & Record<string, unknown>
  );
}

export function getAuthResponseUser(response: AuthResponse): User {
  const token = getOptionalAuthResponseToken(response);

  if (response.user && typeof response.user === "object") {
    const profile = response.user as User & Record<string, unknown>;
    const fallbackUser = createUserFromFlatAuthResponse(profile, token ?? "");
    const firstName =
      typeof profile.firstName === "string"
        ? profile.firstName.trim()
        : fallbackUser.firstName ?? "";
    const surname =
      typeof profile.surname === "string"
        ? profile.surname.trim()
        : fallbackUser.surname ?? "";
    const displayName =
      typeof profile.displayName === "string" && profile.displayName.trim()
        ? profile.displayName.trim()
        : [firstName, surname].filter(Boolean).join(" ") ||
          (typeof profile.companyName === "string" ? profile.companyName : "") ||
          (typeof profile.fullName === "string" ? profile.fullName : "") ||
          (typeof profile.email === "string" ? profile.email : "") ||
          fallbackUser.displayName ||
          "Användare";
    const accountType =
      typeof profile.accountType === "string"
        ? normalizeAccountTypeValue(profile.accountType)
        : fallbackUser.accountType;

    return {
      ...fallbackUser,
      ...profile,
      id:
        typeof profile.id === "number"
          ? profile.id
          : fallbackUser.id,
      email:
        typeof profile.email === "string" && profile.email.trim()
          ? profile.email.trim()
          : fallbackUser.email,
      accountType: accountType ?? fallbackUser.accountType,
      displayName,
      createdAt:
        typeof profile.createdAt === "string"
          ? profile.createdAt
          : fallbackUser.createdAt,
      verified:
        typeof profile.verified === "boolean"
          ? profile.verified
          : Boolean(profile.verifiedStudent ?? profile.verifiedIdentity),
      schoolId:
        typeof profile.schoolId === "number"
          ? profile.schoolId
          : typeof profile.school_id === "number"
            ? profile.school_id
            : fallbackUser.schoolId,
      firstName,
      surname,
    };
  }

  const responseLike = response as unknown as Record<string, unknown>;
  if (
    typeof responseLike === "object" &&
    responseLike !== null &&
    (typeof responseLike.accountType === "string" ||
      typeof responseLike.email === "string" ||
      token)
  ) {
    return createUserFromFlatAuthResponse(responseLike, token ?? "");
  }

  throw new Error("Backend skickade ingen anvandare i auth-svaret.");
}

export function isAuthResponse(response: unknown): response is AuthResponse {
  if (typeof response !== "object" || response === null || Array.isArray(response)) {
    return false;
  }

  const responseLike = response as Partial<AuthResponse> & Record<string, unknown>;
  return TOKEN_KEYS.some(
    (key) =>
      typeof responseLike[key] === "string" &&
      String(responseLike[key]).trim().length > 0
  );
}

/**
 * Skiljer ett BankID-pollsvar från Frejas nakna statussträng och från
 * inloggningssvaret. Kännetecknet är ett objekt med ett `status`-fält —
 * inloggningssvaret bär i stället en token, och det testas först av anroparen.
 */
export function isBankIdPollResponse(
  response: unknown
): response is BankIdPollResponse {
  if (typeof response !== "object" || response === null || Array.isArray(response)) {
    return false;
  }

  return typeof (response as Partial<BankIdPollResponse>).status === "string";
}

export function isGoogleRegisterResponse(
  response: unknown
): response is GoogleRegisterResponse {
  if (typeof response !== "object" || response === null || Array.isArray(response)) {
    return false;
  }

  const responseLike = response as Partial<GoogleRegisterResponse> &
    Record<string, unknown>;

  return (
    responseLike.user === undefined &&
    typeof responseLike.email === "string" &&
    responseLike.email.trim().length > 0 &&
    TOKEN_KEYS.some(
      (key) =>
        typeof responseLike[key] === "string" &&
        String(responseLike[key]).trim().length > 0
    )
  );
}

function persistAuthToken(token: string | null) {
  if (typeof window === "undefined" || !token) {
    return;
  }

  setStoredAuthToken(token);
}

function clearAuthToken() {
  if (typeof window === "undefined") {
    return;
  }

  setStoredAuthToken(null);
}

export function isStudentRegistrationResponse(
  response: RegisterResponse
): response is { authRef: string } {
  return (
    typeof response === "object" &&
    response !== null &&
    "authRef" in response &&
    typeof response.authRef === "string" &&
    response.authRef.trim().length > 0
  );
}

async function fetchCurrentSession(token?: string | null): Promise<AuthResponse> {
  const session = await apiClient<AuthResponse>("/auth/me", {}, token);
  const refreshedToken = getOptionalAuthResponseToken(session);
  persistAuthToken(refreshedToken);
  return session;
}

export const authService = {
  session: async (token?: string | null): Promise<AuthResponse> => {
    return fetchCurrentSession(token);
  },

  me: async (token?: string | null): Promise<User> => {
    return getAuthResponseUser(await fetchCurrentSession(token));
  },

  login: async (payload: LoginRequest): Promise<AuthResponse> => {
    const email = payload.email.trim();
    if (!email || !payload.password) {
      throw new Error("Fyll i e-postadress och lösenord.");
    }

    return apiClient<AuthResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password: payload.password }),
      auth: false,
    });
  },

  adminLogin: async (payload: LoginRequest): Promise<AuthResponse> => {
    if (typeof window !== "undefined" && !isAdminSubdomain()) {
      throw new Error("Admin-inloggning är endast tillgänglig på admin-subdomänen.");
    }

    const email = payload.email.trim();
    if (!email || !payload.password) {
      throw new Error("Fyll i e-postadress och losenord.");
    }

    return apiClient<AuthResponse>("/auth/admin-login", {
      method: "POST",
      body: JSON.stringify({ email, password: payload.password }),
      auth: false,
    });
  },

  googleLogin: async (
    payload: GoogleAuthRequest
  ): Promise<AuthResponse | GoogleRegisterResponse> => {
    const googleIdToken = payload.googleIdToken.trim();
    if (!googleIdToken) {
      throw new Error("Google-token krävs.");
    }

    return apiClient<AuthResponse | GoogleRegisterResponse>("/auth/google/login", {
      method: "POST",
      body: JSON.stringify({ googleIdToken }),
      auth: false,
    });
  },

  quickRegister: async (
    payload: QuickRegisterRequest
  ): Promise<AuthResponse | QuickRegisterResponse> => {
    const email = payload.email.trim();
    const password = payload.password.trim();
    if (!email || !password) {
      throw new Error("E-postadress och lösenord krävs.");
    }

    return apiClient<AuthResponse | QuickRegisterResponse>("/auth/quick-register", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      auth: false,
    });
  },

  registerStudent: async (): Promise<FrejaAuthRef> => {
    return apiClient<FrejaAuthRef>("/auth/register-student", {
      method: "POST",
    });
  },

  registerWorker: async (
    payload: RegisterRequest,
    options: { auth?: boolean } = {}
  ): Promise<AuthResponse> => {
    if (payload.accountType !== "company") {
      throw new Error("Aktuell backend stöder bara företagsregistrering via register-worker.");
    }

    const requestPayload = compactObject({
      accountType: payload.accountType,
      email: payload.email.trim(),
      password: payload.password,
      firstName: payload.firstName?.trim(),
      surname: payload.surname?.trim(),
      phone: payload.phone?.trim(),
      companyName: payload.companyName?.trim(),
      roleName: payload.roleName?.trim(),
      fullName: payload.fullName?.trim(),
    });

    return apiClient<AuthResponse>("/auth/register-worker", {
      method: "POST",
      body: JSON.stringify(requestPayload),
      auth: options.auth ?? false,
    });
  },

  register: async (payload: RegisterRequest): Promise<RegisterResponse> => {
    if (payload.accountType !== "student") {
      return authService.registerWorker(payload);
    }

    return authService.quickRegister({
      email: payload.email,
      password: payload.password,
    });
  },

  googleRegister: async (
    payload: GoogleAuthRequest
  ): Promise<GoogleRegisterResponse> => {
    const googleIdToken = payload.googleIdToken.trim();

    if (!googleIdToken) {
      throw new Error("Google-token krävs.");
    }

    return apiClient<GoogleRegisterResponse>("/auth/google/register", {
      method: "POST",
      body: JSON.stringify({ googleIdToken }),
      auth: false,
    });
  },

  frejaRegister: async (): Promise<FrejaRegisterResponse> => {
    return apiClient<FrejaRegisterResponse>("/auth/freja/register", {
      method: "POST",
      auth: false,
    });
  },

  verifyIdentity: async (): Promise<FrejaAuthRef> => {
    return authService.registerStudent();
  },

  /**
   * Pollar en pågående identitetsverifiering, oavsett leverantör — backend
   * slår upp vilken det är från referensen.
   *
   * Svaret är polymorft och har tre former:
   * - en statussträng (PENDING/MATCHES/...) för Freja,
   * - hela inloggningen ({ accessToken, user }, HTTP 201) när en
   *   Freja-only-registrering går igenom — detektera med isAuthResponse(),
   * - ett BankIdPollResponse-objekt för BankID, som också bär en ny QR-kod —
   *   detektera med isBankIdPollResponse().
   */
  pollAuthStatus: async (
    authRef: string
  ): Promise<FrejaAuthStatus | AuthResponse | BankIdPollResponse> => {
    return apiClient<FrejaAuthStatus | AuthResponse | BankIdPollResponse>(
      `/auth/poll/${pathSegment(authRef)}`,
      { auth: false }
    );
  },

  /**
   * Startar BankID-verifiering för det inloggade quick-register-kontot.
   * Motsvarigheten till registerStudent(), men svaret bär också den första
   * QR-koden: BankIDs QR genereras av BankID och kan inte härledas ur
   * referensen så som Frejas kan.
   */
  startBankIdVerification: async (): Promise<BankIdStartResponse> => {
    return apiClient<BankIdStartResponse>("/auth/bankid/start", {
      method: "POST",
    });
  },

  /** Avbryter en pågående BankID-transaktion, även i användarens app. */
  cancelBankIdVerification: async (sessionId: string): Promise<void> => {
    await apiClient<void>(
      `/auth/bankid/cancel/${pathSegment(sessionId)}`,
      { method: "POST" }
    );
  },

  /**
   * Vilka e-legitimationer miljön faktiskt kan erbjuda. BankID svarar false
   * tills nycklarna är satta, och klienten döljer valet så länge.
   */
  getIdentityProviders: async (): Promise<IdentityProviderAvailability> => {
    return apiClient<IdentityProviderAvailability>("/auth/identity/providers", {
      auth: false,
    });
  },

  startPasswordReset: async (
    payload: StartPasswordResetRequest
  ): Promise<void> => {
    const userEmail = payload.userEmail.trim();
    if (!userEmail) {
      throw new Error("Ange e-postadressen för kontot.");
    }

    await apiClient<void>("/auth/reset-password", {
      method: "POST",
      auth: false,
      body: JSON.stringify({ userEmail }),
    });
  },

  resetPassword: async (payload: PasswordResetFinalRequest): Promise<void> => {
    const resetId = payload.resetId.trim();
    const newPassword = payload.newPassword.trim();

    if (!resetId || !newPassword) {
      throw new Error("Återställnings-id och nytt lösenord krävs.");
    }

    await apiClient<void>("/auth/reset-password", {
      method: "PUT",
      auth: false,
      body: JSON.stringify({ resetId, newPassword }),
    });
  },

  verifyEmail: async (payload: VerifyEmailRequest): Promise<void> => {
    const email = payload.email.trim();
    if (!email) {
      throw new Error("Ange e-postadressen som ska verifieras.");
    }

    await apiClient<void>("/auth/verify-email", {
      method: "POST",
      auth: false,
      body: JSON.stringify({ email }),
    });
  },

  finalizeEmailVerification: async (id: string): Promise<void> => {
    await apiClient<void>(`/auth/verify-email/finalize/${pathSegment(id)}`, {
      auth: false,
    });
  },

  verifyStudentDeprecated: async (): Promise<Record<string, unknown>> => {
    return apiClient<Record<string, unknown>>("/auth/verify-student", {
      method: "POST",
      auth: false,
    });
  },

  updateProfile: async (data: UpdateUserRequest): Promise<User> => {
    const payload = compactObject<UpdateUserRequest>({
      firstName: data.firstName,
      surname: data.surname,
      phone: data.phone,
      aboutText: data.aboutText,
      description: data.description,
      streetAddress: data.streetAddress,
      postalCode: data.postalCode,
      postalCity: data.postalCity,
      addressCountry: data.addressCountry,
      // Skickas som helhet eller inte alls: backend ersätter de sparade
      // uppgifterna med objektet, och null i ett fält nollställer det.
      // compactObject tar bara bort undefined, så nullarna inuti når fram.
      applicantDetails: data.applicantDetails,
    });

    return apiClient<User>("/auth/me", {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    void data;
    throw new Error(
      "Lösenordsbyte för inloggade användare finns inte i aktuell API-version. Använd lösenordsåterställning i stället."
    );
  },

  deleteMe: async (): Promise<Record<string, unknown> | UserDeleteFailureDTO> => {
    return apiClient<Record<string, unknown> | UserDeleteFailureDTO>("/auth/me", {
      method: "DELETE",
    });
  },

  logout: () => {
    clearAuthToken();
  },
};
